import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import { clearAuth } from '../utils/auth';

// Create auth context
const AuthContext = createContext();

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({ accounts: [], sites: [], devices: [] });
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const navigate = useNavigate();

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    localStorage.setItem('user', JSON.stringify(updatedUser));
  };
  
  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      try {
        const storedToken = localStorage.getItem('token');
        
        if (!storedToken) {
          setLoading(false);
          return;
        }
        
        // Verify token and get user data
        const userData = await authService.verifyToken();
        const userPermissions = await authService.getPermissions();
        
        setToken(storedToken);
        setUser(userData);
        setPermissions(userPermissions);
      } catch (error) {
        console.error('Auth initialization error:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };
    
    initAuth();
  }, []);
  
  // Login function
  const login = async (username, password) => {
    try {
      setLoading(true);
      setError(null);
      
      // Call the auth service to login
      const authData = await authService.login(username, password);
      
      // Store auth data
      localStorage.setItem('token', authData.token);
      localStorage.setItem('user', JSON.stringify(authData.user));
      localStorage.setItem('permissions', JSON.stringify(authData.permissions));
      
      setToken(authData.token);
      setUser(authData.user);
      setPermissions(authData.permissions);

      // Force password change flow
      if (authData.user && authData.user.mustChangePassword) {
        navigate('/profile');
        return authData;
      }
      
      // Redirect based on user role
      if (authData.user.role === 'admin' || authData.user.role === 'operator') {
        navigate('/sites');
      } else if (authData.user.role === 'super_user') {
        navigate('/sites');
      } else {
        navigate('/devices');
      }
      
      return authData;
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  // Logout function
  const logout = () => {
    clearAuth();
    setUser(null);
    setToken(null);
    setPermissions({ accounts: [], sites: [], devices: [] });
    navigate('/login');
  };

  const refreshPermissions = async () => {
    try {
      const nextPermissions = await authService.getPermissions();
      setPermissions(nextPermissions || { accounts: [], sites: [], devices: [] });
      return nextPermissions;
    } catch (error) {
      console.error('Error refreshing permissions:', error);
      return null;
    }
  };
  
  // Check if user has a specific role
  const hasRole = (roles) => {
    if (!user) return false;
    return Array.isArray(roles) 
      ? roles.includes(user.role) 
      : user.role === roles;
  };
  
  // Check if user has access to a specific site
  const hasSiteAccess = (siteId) => {
    if (!user || !permissions) return false;
    
    // Admin and operator users have access to all sites
    if (user.role === 'admin' || user.role === 'operator') return true;

    // Check if site is in user's permissions
    return permissions.sites.some(site => site.id === parseInt(siteId));
  };
  
  // Check if user has access to a specific device
  const hasDeviceAccess = (deviceId) => {
    if (!user || !permissions) return false;
    
    // Admin and operator users have access to all devices
    if (user.role === 'admin' || user.role === 'operator') return true;

    // Check if device is in user's permissions
    return permissions.devices.some(device => device.id === parseInt(deviceId));
  };
  
  // Check if user has specific permissions
  const hasPermission = (permissionName) => {
    return permissions && permissions[permissionName] === true;
  };

  // Check if user has multiple permissions (all of them)
  const hasAllPermissions = (permissionNames = []) => {
    return permissionNames.every(permission => hasPermission(permission));
  };

  // Check if user has at least one of the permissions
  const hasAnyPermission = (permissionNames = []) => {
    return permissionNames.some(permission => hasPermission(permission));
  };

  // Context value
  const value = {
    user,
    token,
    permissions,
    loading,
    error,
    login,
    updateUser,
    logout,
    refreshPermissions,
    hasRole,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    hasSiteAccess,
    hasDeviceAccess
  };
  
  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
